import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronsUpDown } from 'lucide-react';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { useToast } from '../../componentes/toast';
import { definirFiltroEmpresa, mensagemDeErro } from '../api';
import { useSessao, type EmpresasDaSessao } from '../sessao';

/** Chave do item "Minhas verticais" (sem filtro: as empresas permitidas somadas). */
export const TODAS_AS_EMPRESAS = 'todas';

export interface ItemEmpresa {
  chave: string;
  rotulo: string;
  atual: boolean;
}

/**
 * Itens do filtro de empresa (função pura, testada em `tests/seletor-empresa.test.ts`). Com uma empresa só (ou nenhuma)
 * o filtro não aparece (null). V2 verticais: "Minhas verticais" vem primeiro para todos (sem `nucleo.consolidado`).
 */
export function itensSeletorEmpresa(e: EmpresasDaSessao | undefined, rotuloTodas: string): ItemEmpresa[] | null {
  // Val1 (28/10): com a troca de empresa desligada no workspace, o seletor não aparece em lugar nenhum.
  if (!e || e.troca === false || e.opcoes.length < 2) return null;
  const itens = e.opcoes.map((o) => ({ chave: o.id, rotulo: o.nome, atual: !e.todas && e.atual === o.id }));
  return [{ chave: TODAS_AS_EMPRESAS, rotulo: rotuloTodas, atual: e.todas }, ...itens];
}

/** O filtro da aba para o item escolhido (null = "Minhas verticais"). */
export const filtroDoItem = (chave: string) => (chave === TODAS_AS_EMPRESAS ? null : chave);

/**
 * Fase 2, W2a; V2 verticais: filtro de empresa da aba. Mesmo `Menu` e o mesmo gatilho do seletor de workspace (setas, Esc,
 * clique fora, foco de volta ao botão). O padrão é "Minhas verticais" (tudo o que a pessoa vê junto); escolher uma empresa
 * vale só nesta aba (sessionStorage, cabeçalho `X-Empresa`) e recarrega os dados abertos. A barra superior o liga na
 * integração.
 */
export function SeletorEmpresa({ className = '' }: { className?: string }) {
  const s = useSessao();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [trocando, setTrocando] = useState(false);
  const itens = itensSeletorEmpresa(s.empresas, t('empresa.todas'));
  if (!itens) return null;
  const atual = itens.find((i) => i.atual);
  const trocar = async (chave: string) => {
    if (trocando) return;
    setTrocando(true);
    try {
      definirFiltroEmpresa(filtroDoItem(chave));
      await qc.invalidateQueries();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setTrocando(false);
    }
  };
  const menu: ItemMenu[] = itens.map((i) => ({
    chave: i.chave,
    rotulo: i.rotulo,
    atual: i.atual,
    aoClicar: () => {
      if (!i.atual) void trocar(i.chave);
    },
  }));
  const nome = atual?.rotulo ?? t('empresa.rotulo');
  // Review r16 visual M1: gatilho de uma linha (32 px) para caber na barra de 40 px; o nome da empresa atual sempre à vista
  // (no celular, na segunda linha do cabeçalho, ao lado da faixa de índices) e o painel largo, ancorado à direita, com o nome
  // inteiro no title de cada item (empresas com o mesmo começo de nome se distinguem).
  return (
    <div data-tour="empresa" className={className} aria-busy={trocando || undefined}>
      <Menu
        rotulo={t('empresa.trocar')}
        itens={menu}
        classePainel="right-0 w-72"
        classeGatilho="flex h-8 w-full max-w-full min-w-0 items-center gap-2 rounded-lg border border-borda bg-sup-2 px-2 text-left text-sm hover:bg-sup-3"
        gatilho={
          <>
            <Building2 className="size-4 shrink-0 text-tinta-3" aria-hidden />
            <span className="min-w-0 flex-1 truncate font-medium text-tinta-1" title={nome} data-empresa-atual="">
              {nome}
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-tinta-3" aria-hidden />
          </>
        }
      />
    </div>
  );
}
